import { Badge, Button } from '@material-ui/core';
import styled from 'styled-components';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getSyncStatesRequest, triggerConfigurationRun } from '../../../api/studykit';
import { StudyPageContext } from '../../../App';
import { useSubjectContext } from '../index';
import useRequest from '../../../api/useRequest';
import { useSnackbar } from 'notistack';
import { Tooltip } from '@dls/react-core';
import { Icon } from '@dls/react-icon';
import { Wheel } from '../../../components/subjects/data/assets';
import { SyncStatePopover } from '../../../components/subjects/data/sync-state-popover';

const StyledBadge = styled(Badge)`
  .MuiBadge-badge {
    right: 16px;
    top: 8px;
  }
`;

export const SyncStateIndicator = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { study } = useContext(StudyPageContext);
  const { subject } = useSubjectContext();
  const { data: syncStates, error } = useRequest(getSyncStatesRequest(study.id, subject.id), {
    refreshInterval: 1500,
  });
  const { enqueueSnackbar } = useSnackbar();

  const isSomethingRunning = syncStates?.some((s) => s.state === 'running') || false;
  const didSomethingFail = syncStates?.some((s) => s.state === 'failed') || false;

  const [tooltipWarningOpen, setTooltipWarningOpen] = useState(false);
  const [isFailedSeen, setFailedSeen] = useState<boolean>();

  useEffect(() => {
    if (didSomethingFail && open && !isFailedSeen) {
      setTooltipWarningOpen(false);
      setFailedSeen(true);
    }
  }, [didSomethingFail, open, isFailedSeen]);

  useEffect(() => {
    setFailedSeen(!didSomethingFail);
    setTooltipWarningOpen(didSomethingFail);
    setTimeout(() => {
      setTooltipWarningOpen(false);
    }, 3000);
  }, [didSomethingFail]);

  const handleOnTriggerConfiguration = useCallback(
    (configId: string) => {
      triggerConfigurationRun(study.id, subject.id, configId)
        .then(() => {
          enqueueSnackbar(
            'Configuration was successfully triggered. Processing should start soon.',
            {
              variant: 'success',
            },
          );
        })
        .catch(() => {
          enqueueSnackbar('Could not manually trigger configuration.', {
            variant: 'error',
          });
        });
    },
    [study.id, subject.id, enqueueSnackbar],
  );

  return (
    <>
      <Tooltip
        open={tooltipWarningOpen}
        title={
          <div style={{ display: 'flex', columnGap: 8, alignItems: 'center' }}>
            <Icon name={'ExclamationMarkCircle'} size={'s'} />
            <span>One or multiple sync attempts failed</span>
          </div>
        }
        arrow
      >
        <StyledBadge color="error" variant="dot" invisible={isFailedSeen}>
          <Button title={'View sync states'} ref={ref} onClick={() => setOpen(!open)}>
            <Wheel spinning={isSomethingRunning} />
          </Button>
        </StyledBadge>
      </Tooltip>
      <SyncStatePopover
        open={open}
        error={error !== undefined}
        anchorElement={ref.current || undefined}
        syncStates={syncStates}
        onTriggerConfiguration={handleOnTriggerConfiguration}
      />
    </>
  );
};
